import type { ChunkSurfaces } from "daedalus-core";
import { DEFAULT_SURFACE, SIXTEENTHS, footstepSurface } from "odyssey-shared";

/**
 * What the ground of the booted map is made of (ADR-085's amendment for P1.9-T84): the surfaces every client chunk
 * carries, assembled once into one byte per tile of the whole map, so asking what a character stands on is one read.
 * A world compiled before surfaces has none in its chunks, and every tile then reads as the default surface.
 */

/**
 * A map's surfaces: its size in tiles, the names its cells index from 1, and one byte per tile, 0 for none.
 */
export interface GroundSurfaces {
  readonly width: number;
  readonly height: number;
  readonly names: readonly string[];
  readonly cells: Uint8Array;
}

/**
 * Assembles a map's surfaces from its chunks, renumbering each chunk's own names into one table for the map.
 *
 * @param chunkOf A chunk's surfaces by its column and row, undefined for a chunk that carries none.
 * @param width Map width in tiles.
 * @param height Map height in tiles.
 * @param chunkSize Chunk size in tiles.
 * @returns The map's surfaces; with no names when no chunk carries any.
 */
export function assembleSurfaces(chunkOf: (cx: number, cy: number) => ChunkSurfaces | undefined, width: number, height: number, chunkSize: number): GroundSurfaces {
  const names: string[] = [];
  const cells = new Uint8Array(width * height);
  const columns = Math.ceil(width / chunkSize);
  const rows = Math.ceil(height / chunkSize);
  for (let cy = 0; cy < rows; cy += 1) {
    for (let cx = 0; cx < columns; cx += 1) {
      const chunk = chunkOf(cx, cy);
      if (chunk === undefined) {
        continue;
      }
      const remap = chunk.names.map((name) => {
        const at = names.indexOf(name);
        return at === -1 ? names.push(name) : at + 1;
      });
      for (let y = 0; y < chunkSize && cy * chunkSize + y < height; y += 1) {
        for (let x = 0; x < chunkSize && cx * chunkSize + x < width; x += 1) {
          const value = chunk.cells[y * chunkSize + x] as number;
          cells[(cy * chunkSize + y) * width + cx * chunkSize + x] = value === 0 ? 0 : (remap[value - 1] ?? 0);
        }
      }
    }
  }
  return { width, height, names, cells };
}

/**
 * The surface under a point of the map.
 *
 * @param surfaces The map's surfaces, or null before a world has loaded.
 * @param x The point across, in sixteenths of a tile.
 * @param y The point down, in sixteenths.
 * @returns The surface's id; the default surface for a tile with none, a point outside the map and no world.
 */
export function surfaceAt(surfaces: GroundSurfaces | null, x: number, y: number): string {
  if (surfaces === null) {
    return DEFAULT_SURFACE;
  }
  const column = Math.floor(x / SIXTEENTHS);
  const row = Math.floor(y / SIXTEENTHS);
  if (column < 0 || row < 0 || column >= surfaces.width || row >= surfaces.height) {
    return DEFAULT_SURFACE;
  }
  return footstepSurface(surfaces.names, surfaces.cells[row * surfaces.width + column] as number);
}
